import React, { useState, SyntheticEvent, useEffect, useRef, RefObject, useMemo, useContext } from 'react';
import gql from 'graphql-tag';
import { ImageUpload, UploadInfo } from '../../libs/gs-component-image-upload';
import Icon, { IconButton } from '@xo-union/tk-component-icons';
import { Caption } from '@xo-union/tk-ui-typography';
import PhotoCropper from '../Modals/PhotoCropper';
import { CropProps } from '../PageItemsRail/ImageField';
import { resizeImageW } from '../../helpers/mediaApiUrls';
import {
  useCreateCoverPhotoOnWebsiteMutation,
  useDeleteCoverPhotoOnWebsiteMutation,
  useWeddingWebsiteQuery,
  Wws_PhotoAttributes,
} from '../../generated/graphql';
import { imageUrlToImgElement } from '../../helpers/images/processImages';
import { TrackingContext } from '../../TrackingContext';
import { useImageUploader } from '../../helpers/images/useImageUploader';
import { getTargetOrigin } from '../../helpers/getTargetOrigin';
import {
  HORIZONTAL_ICON,
  HORIZONTAL_RATIO,
  LAYOUT_ASPECT_RATIO,
  VERTICAL_ICON,
  VERTICAL_RATIO,
} from '../Modals/PhotoCropper/LayoutToggle';
import CoverPhotoItem from './CoverPhotoItem';
import ZeroState from './ZeroState';
import { IconToggle, IconToggleContainer } from '../PreviewPage/IconToggle';
import styles from './coverPhoto.scss';

export const createCoverPhotoOnWebsiteMutation = gql`
  mutation createCoverPhotoOnWebsiteMutation($attributes: WWS_PhotoAttributes!) {
    createCoverPhotoOnWebsite(attributes: $attributes) {
      id
      coverPhoto {
        mediaApiId
        mediaUrl
        cropX
        cropY
        width
        height
        originalMediaUrl
      }
    }
  }
`;

export const deleteCoverPhotoOnWebsiteMutation = gql`
  mutation deleteCoverPhotoOnWebsiteMutation {
    deleteCoverPhotoOnWebsite {
      id
      coverPhoto {
        mediaApiId
        mediaUrl
        cropX
        cropY
        width
        height
        originalMediaUrl
      }
    }
  }
`;

interface Props {
  pageId: number;
  iframeRef?: RefObject<HTMLIFrameElement>;
  onSave?: () => void;
}

const CoverPhoto: React.FC<Props> = ({ pageId, iframeRef, onSave = () => {} }) => {
  const [cropperFile, setCropperFile] = useState<HTMLImageElement>();
  const [loadingUpdatedImage, setLoadingUpdatedImage] = useState(false);

  const [createOrUpdateCoverPhoto] = useCreateCoverPhotoOnWebsiteMutation();
  const [deleteCoverPhoto] = useDeleteCoverPhotoOnWebsiteMutation();
  const { data } = useWeddingWebsiteQuery();
  const [imageData, setImageData] = useState(data?.weddingWebsite.coverPhoto);
  useEffect(() => {
    setImageData(data?.weddingWebsite.coverPhoto);
  }, [data]);
  const { imageUpload } = useImageUploader();
  const alertRef = useRef<any>(null);
  const { track } = useContext(TrackingContext);
  const [horizontalLayout, setHorizontalLayout] = React.useState<boolean>(true);

  const photoLayout = useMemo(() => {
    if (imageData?.width && imageData?.height && imageData?.width < imageData?.height) {
      setHorizontalLayout(false);
      return VERTICAL_ICON;
    }
    setHorizontalLayout(true);
    return HORIZONTAL_ICON;
  }, [imageData]);

  const [layoutRatioName, setLayoutRatioName] = useState<string>(photoLayout);
  const [layoutRatioValue, setLayoutRatioValue] = useState<number>(() =>
    photoLayout === HORIZONTAL_ICON ? HORIZONTAL_RATIO : VERTICAL_RATIO
  );

  const closeCropper = () => {
    // update layout toggle switch
    if (imageData) {
      if ((imageData?.width || 0) >= (imageData?.height || 0)) {
        setHorizontalLayout(true);
      } else {
        setHorizontalLayout(false);
      }
    }

    setCropperFile(undefined);
  };

  const trackCoverPhotoAction = (action: string, ratioName: string) => {
    track('Photo Clicked', {
      action: action,
      layout_selection: ratioName === HORIZONTAL_ICON ? 'horizontal' : 'vertical',
      photo_type: 'cover photo',
    });
  };

  const openCropperWithLayout = async (uploadInfo: UploadInfo, ratioName: string) => {
    setLayoutRatioName(ratioName);
    setLayoutRatioValue(LAYOUT_ASPECT_RATIO[ratioName]);
    await openCropper(uploadInfo, ratioName);
  };

  const openCropper = async (uploadInfo: UploadInfo, ratioName?: string) => {
    const { imagePreviewUrl } = uploadInfo;
    const hasCoverPhoto = Boolean(imageData?.mediaUrl);

    const openCropperAction = cropperFile || hasCoverPhoto ? 'replace_image' : 'open_modal';
    trackCoverPhotoAction(openCropperAction, ratioName || layoutRatioName);
    if (!imagePreviewUrl || typeof imagePreviewUrl !== 'string') return;
    const img = await imageUrlToImgElement(imagePreviewUrl);
    setCropperFile(img);
  };

  const showError = (message?: string) => {
    if (!alertRef.current) return;
    alertRef.current.showAndDismiss({
      type: 'error',
      content: message || 'There was an error uploading your image. Try again.',
      waitTime: 3000,
    });
  };

  const editCoverPhoto = async () => {
    if (!imageData?.mediaUrl) {
      return;
    }

    // open cropper with selected layout
    const ratioName = photoLayout;
    openCropperWithLayout(
      {
        imagePreviewUrl: imageData.mediaUrl,
        file: undefined,
      },
      ratioName
    );
  };

  const editImage = async (e: SyntheticEvent) => {
    e.preventDefault();
    editCoverPhoto();
  };

  const updateIframe = (type: any, data?: any) => {
    //update apollo weddingwebsite cache

    if (iframeRef && iframeRef.current?.contentWindow) {
      switch (type) {
        case 'updatePage': {
          iframeRef.current.contentWindow.postMessage(JSON.stringify({ type: 'updatePage', data }), getTargetOrigin());
          break;
        }
        case 'reload':
        default: {
          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({
              type: 'reload',
            }),
            getTargetOrigin()
          );
        }
      }
    }
  };

  const saveImage = async ({ file, crop, rotation }: CropProps) => {
    /**
     * Currently, we are experiencing issues between clients where the cover photo rotation is off.
     * On the web, we rotate, upload the already rotated image, and save the rotation value.
     * When displaying the image, we use the mediaURL from the backend.
     * That URL gets built on call and includes the rotation as part of the URL.
     * This applies the rotation to the image twice, causing the issue of the rotation being off.
     * The solution to keeping it backward compatible for the couples that have already
     *  fixed the image is to set the rotation value to 0 on creates or updates.
     *
     * fyi iOS is also following this path, uploading the already rotated image and saving a rotation value of 0.
     */
    try {
      let imageAttrs: Wws_PhotoAttributes = {
        cropX: crop.x,
        cropY: crop.y,
        width: crop.width,
        height: crop.height,
        rotation: 0,
      };

      setLoadingUpdatedImage(true);

      const res = await imageUpload({ file });
      imageAttrs.mediaApiId = res.data?.mediaApiId;

      const { data } = await createOrUpdateCoverPhoto({
        variables: {
          attributes: imageAttrs,
        },
      });

      const coverPhotoData = data?.createCoverPhotoOnWebsite?.coverPhoto;

      setLoadingUpdatedImage(false);
      setImageData(coverPhotoData);

      // update layout toggle switch
      if ((coverPhotoData?.width || 0) >= (coverPhotoData?.height || 0)) {
        setHorizontalLayout(true);
      } else {
        setHorizontalLayout(false);
      }

      const cropbox = `${crop.x}.${crop.y}.${crop.x + crop.width}.${crop.y + crop.height}`;
      const coverPhoto = {
        cropBox: cropbox,
        height: coverPhotoData?.height,
        path: coverPhotoData?.originalMediaUrl,
        width: coverPhotoData?.width,
      };

      updateIframe('updatePage', {
        coverPhoto,
        coverPhotoPathWithCropbox: coverPhotoData?.mediaUrl,
      });

      trackCoverPhotoAction('upload_photo', layoutRatioName);
      setCropperFile(undefined);
      onSave();
    } catch (error) {
      setLoadingUpdatedImage(false);
      showError();
    }
  };

  const deleteImage = async (e: SyntheticEvent) => {
    setLoadingUpdatedImage(true);
    e.stopPropagation();
    e.preventDefault();
    const { data } = await deleteCoverPhoto();
    setLoadingUpdatedImage(false);
    setImageData(data?.deleteCoverPhotoOnWebsite.coverPhoto);
    updateIframe('reload');
    trackCoverPhotoAction('delete_photo', layoutRatioName);
  };

  const handleLayoutChange = (ratioName: string) => {
    if (!imageData?.mediaUrl) {
      return;
    }

    // update layout toggle switch
    setHorizontalLayout(!horizontalLayout);

    // open cropper with selected layout
    openCropperWithLayout(
      {
        imagePreviewUrl: imageData.mediaUrl,
        file: undefined,
      },
      ratioName
    );
  };

  const renderContent = () => {
    const hasCoverPhoto = Boolean(imageData?.mediaUrl);

    if (hasCoverPhoto) {
      return (
        <>
          <CoverPhotoItem
            coverPhotoUrl={resizeImageW(imageData?.mediaUrl, 300, true)}
            onEditCoverPhoto={editCoverPhoto}
            onReplaceCoverPhoto={openCropper}
            isHorizontalLayout={photoLayout === HORIZONTAL_ICON}
          />
          <div className={styles.coverPhotoLayoutItem}>
            <div className={styles.coverPhotoLayoutSwitch}>
              <Caption>
                <span className={styles.desktopTitle}>Cover photo layout</span>
                <span className={styles.mobileTitle}>Page layout</span>
              </Caption>
              <IconToggleContainer size="md" changeSelectedPreview={handleLayoutChange} className={styles.layoutToggle}>
                <IconToggle selected={horizontalLayout} icon={HORIZONTAL_ICON} iconLabel="Horizontal" />
                <IconToggle selected={!horizontalLayout} icon={VERTICAL_ICON} iconLabel="Vertical" />
              </IconToggleContainer>
            </div>
            <div className={styles.divider} />
          </div>
        </>
      );
    } else {
      return (
        <div className={styles.imageUpload}>
          {hasCoverPhoto ? (
            <ImageUpload onUploadEnded={openCropper}>
              <div
                data-testid="cover-photo"
                className={styles.imageContainer}
                style={{ backgroundImage: `url(${resizeImageW(imageData?.mediaUrl, 1200, true)})` }}
              >
                <div className={styles.overlay}>
                  <div className={`${styles.coverAction}`} onClick={editImage} data-testid="cp-overlay">
                    <div className={`${styles.coverIcon} ${styles.uploadIcon}`}>
                      <Icon size="lg" name="edit" />
                    </div>
                    <div className={`${styles.coverIconLabel}`}>Edit</div>
                  </div>
                  <div className={`${styles.coverAction}`} onClick={deleteImage} data-testid="delete-cp">
                    <div className={`${styles.coverIcon}`}>
                      <IconButton size="lg" name="delete" aria-label="Delete Image" />
                    </div>
                    <div className={`${styles.coverIconLabel}`}>Delete</div>
                  </div>
                </div>
              </div>
            </ImageUpload>
          ) : (
            <ZeroState onAddCoverPhotoClick={openCropper} onOpenCropperWithLayout={openCropperWithLayout} />
          )}
        </div>
      );
    }
  };

  return (
    <>
      {renderContent()}
      {cropperFile && (
        <PhotoCropper
          ref={alertRef}
          cropperInfo={{
            file: cropperFile,
            onSave: saveImage,
          }}
          onCancel={closeCropper}
          pageId={pageId}
          itemType="WeddingPage"
          isCoverPhoto
          primaryActionText="Save"
          secondaryActionText="Cancel"
          loading={loadingUpdatedImage}
          defaultLayoutRatioValue={layoutRatioValue}
          defaultLayoutRatioName={layoutRatioName}
          onDeletePhotoHandler={deleteImage}
          onCloseCropper={closeCropper}
          onAddPhoto={openCropper}
          trackCoverPhotoAction={trackCoverPhotoAction}
        />
      )}
    </>
  );
};

export default CoverPhoto;
